import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle,
  ArrowRight,
  Shield,
  Zap,
  Star,
  Building2,
  TrendingUp,
  Users,
  Briefcase,
  Home,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';

const OFFER_END_DATE = new Date('2026-04-25T00:00:00+02:00');

const PRICE_ID = 'price_1TB8pJEKanECu7bu9S7Iz4ii';
const COUPON_CODE = 'promo_1TMjOlEKanECu7buFffQdSAD';

const FEATURES = [
  'Analyses DPE illimitées',
  'Questions contextuelles avancées',
  'Recommandations personnalisées',
  'Moteur de règles complet (28 règles métier)',
  'Support prioritaire',
  'Rapports détaillés',
  'DPE projetés',
  'Scénarios automatiques "Meilleure lettre" et "Meilleure rentabilité"',
  'Chiffrage des travaux avec ROI par scénario',
  'Rapports PDF avec votre logo et nom d\'entreprise',
];

function useCountdown(targetDate: Date) {
  const calculate = useCallback(() => {
    const now = Date.now();
    const diff = targetDate.getTime() - now;
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    return { days, hours, minutes, seconds, expired: false };
  }, [targetDate]);

  const [countdown, setCountdown] = useState(calculate);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(calculate());
    }, 1000);
    return () => clearInterval(timer);
  }, [calculate]);

  return countdown;
}

function pad(n: number) {
  return String(n).padStart(2, '0');
}

const OffreLancementProPage: React.FC = () => {
  const countdown = useCountdown(OFFER_END_DATE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCheckout = useCallback(async () => {
    if (countdown.expired || loading) return;
    setLoading(true);
    setError(null);
    try {
      const successUrl = `${window.location.origin}/success?source=offre-lancement-pro`;
      const cancelUrl = `${window.location.origin}/offre-lancement-pro`;

      const response = await fetch('/.netlify/functions/payment-create-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          price_id: PRICE_ID,
          promo_code: COUPON_CODE,
          success_url: successUrl,
          cancel_url: cancelUrl,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Erreur lors de la création de la session de paiement.');
      }

      const data = await response.json();
      const url = data.checkout_url || data.url;
      if (!url) throw new Error('URL de paiement manquante.');
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue.');
      setLoading(false);
    }
  }, [countdown.expired, loading]);

  return (
    <div className="min-h-screen bg-white">
      {/* HERO PRINCIPAL */}
      <section className="bg-white py-24 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-6xl md:text-7xl font-extrabold text-gray-900 leading-tight mb-5 tracking-tight">
            6 mois achetés<br />= 6 mois offerts
          </h1>

          <p className="text-2xl text-gray-700 font-medium mb-10">
  12 mois d'accès complet — Pro illimité
</p>

          {/* COMPTEUR */}
          {countdown.expired ? (
            <div className="inline-block bg-gray-900 text-white rounded-xl px-8 py-4 text-xl font-bold mb-10">
              Offre expirée
            </div>
          ) : (
            <div className="flex items-center justify-center gap-3 mb-10">
              {[
                { value: pad(countdown.days), label: 'JOURS' },
                { value: pad(countdown.hours), label: 'HEURES' },
                { value: pad(countdown.minutes), label: 'MINUTES' },
                { value: pad(countdown.seconds), label: 'SECONDES' },
              ].map((unit, i, arr) => (
                <React.Fragment key={unit.label}>
                  <div className="flex flex-col items-center bg-gray-900 rounded-xl w-24 h-24 md:w-28 md:h-28 justify-center shadow-lg">
                    <span className="text-4xl md:text-5xl font-extrabold text-white leading-none tabular-nums">
                      {unit.value}
                    </span>
                    <span className="text-xs text-gray-400 font-semibold tracking-widest mt-1.5 uppercase">
                      {unit.label}
                    </span>
                  </div>
                  {i < arr.length - 1 && (
                    <span className="text-3xl font-bold text-gray-400 select-none -mt-5">:</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* CTA */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm max-w-md mx-auto">
              {error}
            </div>
          )}
          <Button
            size="xl"
            onClick={handleCheckout}
            disabled={countdown.expired || loading}
            loading={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl text-lg font-semibold px-10 py-4 rounded-xl"
          >
            {countdown.expired ? 'Offre expirée' : "Profiter de l'offre — 149,90€ HT"}
            {!countdown.expired && !loading && <ArrowRight className="ml-2 w-5 h-5" />}
          </Button>
          <p className="text-xs text-gray-400 mt-4">
            Engagement annuel — Facturation en une fois — Renouvellement au tarif standard à l'échéance
          </p>
        </div>
      </section>

      {/* BLOC OFFRE CENTRALE */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="relative bg-white rounded-2xl border-2 border-blue-600 shadow-xl p-8 md:p-10">
            {/* Badge top */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <span className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-green-600 text-white text-sm font-semibold px-5 py-1.5 rounded-full shadow-md">
                <Star className="w-4 h-4" />
                Offre de lancement
              </span>
            </div>

            <div className="text-center mb-6 pt-2">
              <div className="flex items-center justify-center mb-3">
                <Building2 className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">DPEWizard — Unlimited Power Pro</h2>
              <p className="text-gray-500 text-base">Toutes les fonctionnalités + branding personnalisé</p>
            </div>

            <div className="text-center mb-6">
              <p className="text-gray-400 line-through text-lg mb-1">299,90€ HT/an</p>
              <div className="flex items-baseline justify-center gap-2">
                <span className="text-5xl font-extrabold text-gray-900">149,90€</span>
                <span className="text-lg text-gray-500 font-medium">HT</span>
              </div>
              <p className="text-gray-500 text-sm mt-2">Paiement unique — 12 mois d'accès complet</p>
              <div className="mt-3 inline-flex items-center gap-1.5 bg-green-100 text-green-700 text-sm font-semibold px-4 py-1.5 rounded-full border border-green-200">
                <CheckCircle className="w-4 h-4" />
                6 mois offerts
              </div>
            </div>

            <ul className="space-y-3 mb-8">
              {FEATURES.map((feature, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 text-sm leading-snug">{feature}</span>
                </li>
              ))}
            </ul>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
                {error}
              </div>
            )}

            <Button
              size="xl"
              onClick={handleCheckout}
              disabled={countdown.expired || loading}
              loading={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl text-base font-semibold"
            >
              {countdown.expired ? 'Offre expirée' : 'Profiter de l\'offre — 149,90€ HT'}
              {!countdown.expired && !loading && <ArrowRight className="ml-2 w-5 h-5" />}
            </Button>

            <p className="text-center text-xs text-gray-400 mt-4">
              Engagement annuel — Facturation en une fois — Renouvellement au tarif standard à l'échéance
            </p>
          </div>
        </div>
      </section>

      {/* POUR QUI */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-3 uppercase leading-tight">
            Un outil pensé pour les professionnels du conseil immobilier et de la rénovation.
          </h2>
          <div className="w-12 h-0.5 bg-blue-500 mx-auto mb-12" />

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Briefcase className="w-7 h-7 text-blue-600" />,
                title: 'Courtiers en travaux',
                text: 'Repartez de chaque rendez-vous avec un document scénarisé à votre nom. Sans avoir eu besoin de maîtriser les travaux sur le bout des doigts.',
              },
              {
                icon: <Home className="w-7 h-7 text-green-600" />,
                title: 'Agents immobiliers',
                text: 'Un DPE F ou G inquiète votre client ? En deux minutes, vous avez une réponse chiffrée à lui donner, et une vente qui aboutit.',
              },
              {
                icon: <TrendingUp className="w-7 h-7 text-blue-500" />,
                title: 'Investisseurs immobiliers',
                text: 'Évaluez le coût réel des travaux et le retour sur investissement avant de vous engager. Pas après.',
              },
            ].map((card, i) => (
              <div key={i} className="border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="mb-4">{card.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{card.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{card.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CE QUI A CHANGE - 3 SCENARIOS */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-3 uppercase leading-tight">
            Trois façons de générer un scénario, selon votre besoin.
          </h2>
          <div className="w-12 h-0.5 bg-blue-500 mx-auto mb-12" />

          <div className="grid md:grid-cols-3 gap-6">
            {/* Scenario 01 */}
            <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="text-xs font-bold text-blue-500 tracking-widest uppercase mb-4">Scénario 01</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Meilleure lettre</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Le moteur identifie la combinaison de travaux qui fait gagner le maximum de lettres DPE pour un budget donné. Idéal pour atteindre un seuil réglementaire.
              </p>
            </div>

            {/* Scenario 02 - featured */}
            <div className="bg-blue-600 rounded-2xl p-8 shadow-lg text-white relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                  Populaire
                </span>
              </div>
              <div className="text-xs font-bold text-blue-200 tracking-widest uppercase mb-4">Scénario 02</div>
              <h3 className="text-xl font-bold text-white mb-3">Meilleure rentabilité</h3>
              <p className="text-blue-100 text-sm leading-relaxed">
                Le moteur calcule le ROI de chaque action et sélectionne les travaux offrant le meilleur rapport gain énergétique / coût investi.
              </p>
            </div>

            {/* Scenario 03 */}
            <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="text-xs font-bold text-blue-500 tracking-widest uppercase mb-4">Scénario 03</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Sur mesure</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Composez librement votre propre scénario en sélectionnant les postes de travaux souhaités. Le simulateur recalcule en temps réel la nouvelle lettre et le coût total.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TÉMOIGNAGES */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-3 uppercase">
            Ils l'utilisent, ils en parlent.
          </h2>
          <div className="w-12 h-0.5 bg-blue-500 mx-auto mb-12" />

          <img
            src="/assets/DPEWizard_testimonials_nonretina.png"
            srcSet="/assets/DPEWizard_testimonials_nonretina.png 1x, /assets/DPEWizard_testimonials_Retina.png 2x"
            alt="Témoignages clients DPEWizard - Retours d'expérience sur l'optimisation énergétique"
            className="w-full h-auto object-contain"
            loading="lazy"
          />
        </div>
      </section>

      {/* CHIFFRES CLÉS */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-12 uppercase">
            Pourquoi choisir DPEWizard Pro ?
          </h2>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              {
                icon: <Shield className="w-8 h-8 text-blue-600 mx-auto mb-3" />,
                stat: '40%',
                label: 'des DPE contiennent des erreurs détectables',
              },
              {
                icon: <Zap className="w-8 h-8 text-green-600 mx-auto mb-3" />,
                stat: '2 minutes',
                label: 'pour une analyse complète',
              },
              {
                icon: <Users className="w-8 h-8 text-blue-500 mx-auto mb-3" />,
                stat: '+30%',
                label: 'de taux de closing pour les professionnels',
              },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
                {item.icon}
                <div className="text-4xl font-extrabold text-gray-900 mb-2">{item.stat}</div>
                <p className="text-gray-600 text-sm leading-relaxed">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-green-600 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold mb-3 uppercase leading-tight">
            Passez à la version Pro maintenant.
          </h2>
          <div className="w-12 h-0.5 bg-white/50 mx-auto mb-6" />
          <p className="text-lg text-white/90 mb-8">
            149,90€ HT pour 12 mois. Offre valable 7 jours.
          </p>

          {countdown.expired ? (
            <div className="inline-block bg-white/20 rounded-xl px-8 py-4 text-white font-semibold text-lg">
              Offre expirée
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 bg-white/10 border border-white/30 text-white rounded-lg px-4 py-3 text-sm max-w-md mx-auto">
                  {error}
                </div>
              )}
              <Button
                size="xl"
                onClick={handleCheckout}
                disabled={loading}
                loading={loading}
                className="bg-white text-blue-700 hover:bg-blue-50 font-semibold shadow-lg hover:shadow-xl border-0"
              >
                Profiter de l'offre — 149,90€ HT
                {!loading && <ArrowRight className="ml-2 w-5 h-5" />}
              </Button>
              <p className="text-white/70 text-sm mt-4">
                Engagement annuel — Facturation en une fois — Renouvellement au tarif standard à l'échéance
              </p>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default OffreLancementProPage;
